import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sidebar, SidebarBody, SidebarLink, useSidebar } from './Sidebar';
import DashboardTutorial from './DashboardTutorial';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../i18n/index.jsx';
import { 
  Home, 
  User, 
  Settings, 
  LogOut, 
  CreditCard,
  BarChart3,
  X,
  Download
} from 'lucide-react';

// Composant pour la popup d'extension non détectée
function ExtensionNotificationPopup({ isVisible, onDismiss, t }) {
  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 150, scale: 0.8 }}
        animate={{ 
          opacity: 1, 
          y: 0, 
          scale: 1,
          transition: {
            type: "spring",
            damping: 25,
            stiffness: 300,
            duration: 0.6
          }
        }}
        exit={{ 
          opacity: 0, 
          y: 100, 
          scale: 0.9,
          transition: { duration: 0.3 }
        }}
        className="fixed bottom-4 left-4 right-4 md:bottom-6 md:right-6 md:left-auto z-50 max-w-md md:mx-0"
      >
        <motion.div 
          className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-slate-900/98 to-slate-800/98 backdrop-blur-xl p-4 md:p-6 shadow-2xl ring-2 ring-emerald-500/20"
          whileHover={{ 
            scale: 1.02,
            boxShadow: "0 25px 50px -12px rgba(16, 185, 129, 0.25)"
          }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        >
          {/* Glow effect background */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-emerald-400/5 to-emerald-600/10 blur-xl"></div>
          
          {/* Content */}
          <div className="relative">
            <div className="flex items-start gap-4">
              <motion.div 
                className="flex-shrink-0 mt-1"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ 
                  scale: 1, 
                  rotate: 0,
                  transition: { delay: 0.3, duration: 0.5 }
                }}
              >
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 flex items-center justify-center ring-2 ring-emerald-500/30">
                  <motion.div
                    animate={{ 
                      rotate: [0, 360],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ 
                      rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                      scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                    }}
                  >
                    <Download className="h-6 w-6 text-emerald-400" />
                  </motion.div>
                </div>
              </motion.div>
              
              <div className="flex-1 min-w-0">
                <motion.h4 
                  className="text-base md:text-lg font-bold text-white mb-2 flex items-center gap-2 flex-wrap"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ 
                    opacity: 1, 
                    x: 0,
                    transition: { delay: 0.4, duration: 0.4 }
                  }}
                >
                  {t('extension.notification.title')}
                  <motion.span 
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/20 text-red-300 text-xs font-medium border border-red-500/30"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div className="h-1.5 w-1.5 rounded-full bg-red-400"></div>
                    {t('extension.notification.notDetected')}
                  </motion.span>
                </motion.h4>
                
                <motion.p 
                  className="text-xs md:text-sm text-slate-300 mb-3 md:mb-4 leading-relaxed"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    transition: { delay: 0.5, duration: 0.4 }
                  }}
                >
                  {t('extension.notification.description')}
                </motion.p>
                
                <motion.div 
                  className="flex flex-col sm:flex-row gap-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    transition: { delay: 0.6, duration: 0.4 }
                  }}
                >
                  <motion.a 
                    href="https://chromewebstore.google.com/detail/llacahflaikdpllhjolamccddjiceelb?utm_source=item-share-cb"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-400 text-slate-950 text-xs md:text-sm font-semibold hover:from-emerald-400 hover:to-emerald-300 transition-all duration-200 shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Download className="h-4 w-4" />
                    {t('extension.notification.installNow')}
                  </motion.a>
                  <motion.button 
                    onClick={onDismiss}
                    className="inline-flex items-center justify-center px-3 md:px-4 py-2 md:py-2.5 rounded-lg bg-slate-700/50 text-slate-300 text-xs md:text-sm font-medium hover:bg-slate-700/70 transition-all duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t('extension.notification.later')}
                  </motion.button>
                </motion.div>
              </div>
              
              <motion.button 
                onClick={onDismiss}
                className="flex-shrink-0 p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all duration-200"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ 
                  opacity: 1, 
                  rotate: 0,
                  transition: { delay: 0.7, duration: 0.3 }
                }}
              >
                <X className="h-5 w-5" />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Composant pour le bouton de déconnexion qui utilise l'état de la sidebar
function LogoutButton({ onLogout, t }) {
  const { open, animate } = useSidebar();
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <button
      onClick={onLogout}
      className="flex items-center gap-2 md:gap-3 w-full py-2.5 md:py-3 px-2.5 md:px-3 rounded-xl text-slate-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200 group"
    >
      <LogOut className="h-5 w-5 flex-shrink-0 group-hover:scale-110 transition-transform" />
      <motion.span
        className="text-xs md:text-sm font-medium whitespace-pre"
        animate={{
          display: isMobile ? "inline-block" : (animate ? (open ? "inline-block" : "none") : "inline-block"),
          opacity: isMobile ? 1 : (animate ? (open ? 1 : 0) : 1),
        }}
      >
        {t('nav.logout')}
      </motion.span>
    </button>
  );
}

// Composant pour la carte utilisateur
function UserCard({ email }) {
  const { open, animate } = useSidebar();
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return (
    <div className="mb-4 rounded-xl bg-white/5 border border-white/10 p-2.5 md:p-3 flex items-center gap-2 md:gap-3">
      <div className="h-8 w-8 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-semibold text-sm flex-shrink-0">
        {(email || 'U').slice(0,1).toUpperCase()}
      </div>
      <motion.div 
        className="overflow-hidden min-w-0"
        animate={{
          display: isMobile ? "block" : (animate ? (open ? "block" : "none") : "block"),
          opacity: isMobile ? 1 : (animate ? (open ? 1 : 0) : 1),
        }}
      >
        <p className="text-xs md:text-sm text-slate-200 truncate">{email || 'Utilisateur'}</p>
        <p className="text-[10px] md:text-xs text-slate-400">Connecté</p>
      </motion.div>
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const { logout } = useAuth();
  const { t } = useI18n();
  const email = typeof window !== 'undefined' ? localStorage.getItem('email') : '';

  // État pour la popup d'extension
  const [showExtensionPopup, setShowExtensionPopup] = useState(false);
  const [extensionDismissed, setExtensionDismissed] = useState(false);

  // Vérifier si l'extension est installée
  useEffect(() => {
    const checkExtension = async () => {
      // Synchronous heuristics first
      const immediate = !!(
        window.HiperyonExtension ||
        window.__Hiperyon_extension_present ||
        document.documentElement?.getAttribute('data-Hiperyon-extension') === '1' ||
        document.querySelector('meta[name="Hiperyon-extension"]') ||
        window.chrome?.runtime?.getManifest?.()?.name?.includes('Hiperyon')
      );

      if (immediate) {
        return true;
      }

      // Otherwise try a postMessage ping and wait for a short response
      return await new Promise((resolve) => {
        let settled = false;
        function handler(e) {
          try {
            const d = e && e.data;
            if (!d) return;
            if (d && (d.type === 'MY_EXTENSION_HERE' || d.type === 'Hiperyon_EXTENSION_PONG' || d.__Hiperyon_extension === true)) {
              settled = true;
              window.removeEventListener('message', handler);
              resolve(true);
            }
          } catch (err) { /* ignore */ }
        }

        window.addEventListener('message', handler);
        try { window.postMessage({ type: 'CHECK_MY_EXTENSION' }, '*'); } catch(e) {}

        // timeout: consider no extension
        const t = setTimeout(() => {
          if (!settled) {
            window.removeEventListener('message', handler);
            resolve(false);
          }
        }, 1200);
      });
    };

    let mounted = true;

    // Run initial check and show popup only if not installed
    (async () => {
      const installed = await checkExtension();
      if (!mounted) return;
      if (!installed) {
        const timer = setTimeout(() => setShowExtensionPopup(true), 2000);
        // clear timer if unmounted or later checks
        const cleanup = () => clearTimeout(timer);
        // ensure cleanup on unmount by attaching to outer return
        // store on local variable isn't necessary here
      }
    })();

    // Re-check periodically
    const interval = setInterval(async () => {
      const installed = await checkExtension();
      if (!installed) return;
      // If installed, hide popup and save state
      setShowExtensionPopup(false);
      setExtensionDismissed(false);
      console.log('Extension installed during session — hiding popup');
    }, 10000);

    return () => { mounted = false; clearInterval(interval); };
  }, []);

  const handleDismissExtensionPopup = () => {
    setShowExtensionPopup(false);
    setExtensionDismissed(true);
    // Sauvegarder temporairement (sera réaffiché lors du prochain refresh)
    localStorage.setItem('Hiperyon-extension-dismissed', 'true');
  };

  const links = [
    {
      label: t('nav.dashboard'),
      href: "/dashboard",
      icon: (
        <Home className="text-slate-400 h-5 w-5 flex-shrink-0 group-hover:text-slate-200 transition-colors" />
      ),
    },
    {
      label: t('nav.stats'),
      href: "/stats",
      icon: (
        <BarChart3 className="text-slate-400 h-5 w-5 flex-shrink-0 group-hover:text-slate-200 transition-colors" />
      ),
    },
    {
      label: t('nav.memories'),
      href: "/souvenirs",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-slate-400 h-5 w-5 flex-shrink-0 group-hover:text-slate-200 transition-colors">
          <path strokeWidth="1.5" d="M4 7a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10.5a1.5 1.5 0 0 1-2.4 1.2l-3.6-2.7a3 3 0 0 0-3.6 0l-3.6 2.7A1.5 1.5 0 0 1 4 17.5V7Z"/>
          <path strokeWidth="1.5" d="M8.5 9h7M8.5 12h4.5"/>
        </svg>
      ),
    },
    {
      label: t('nav.libraries'),
      href: "/libraries",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-slate-400 h-5 w-5 flex-shrink-0 group-hover:text-slate-200 transition-colors">
          <path strokeWidth="1.5" d="M4 19.5V6a2 2 0 0 1 2-2h9l5 5v10.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z"/>
        </svg>
      ),
    },
    {
      label: t('nav.extension'),
      href: "/extension",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-slate-400 h-5 w-5 flex-shrink-0 group-hover:text-slate-200 transition-colors">
          <path strokeWidth="1.5" d="M12 2L2 7V10C2 16 6 20.9 12 22C18 20.9 22 16 22 10V7L12 2M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z"/>
        </svg>
      ),
    },
    {
      label: t('nav.pricing'),
      href: "/pricing",
      icon: (
        <CreditCard className="text-slate-400 h-5 w-5 flex-shrink-0 group-hover:text-slate-200 transition-colors" />
      ),
    },
    {
      label: t('nav.account'),
      href: "/account",
      icon: (
        <User className="text-slate-400 h-5 w-5 flex-shrink-0 group-hover:text-slate-200 transition-colors" />
      ),
    },
    {
      label: t('nav.settings'),
      href: "/settings",
      icon: (
        <div className="relative flex items-center">
          <Settings className="text-slate-400 h-5 w-5 flex-shrink-0 group-hover:text-slate-200 transition-colors" />
          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">Bientôt</span>
        </div>
      ),
    },
  ];  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-black to-slate-950 text-slate-100">
      <Sidebar animate={true}>
        <SidebarBody className="flex flex-col h-full">
          <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
            {/* Logo Section avec texte personnalisé */}
            <div className="mb-6 md:mb-8 px-2">
              <div className="flex items-center gap-3">
                {/* Logo Hiperyon */}
                <div className="h-10 w-10 flex items-center justify-center flex-shrink-0">
                  <img src="/logo_svg.svg" alt="Hiperyon" className="h-10 w-10" />
                </div>
                {/* Nom Hiperyon */}
                <motion.span
                  className="font-bold text-xl text-white whitespace-pre"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  Hiperyon
                </motion.span>
              </div>
            </div>

            {/* User Card - En haut pour mobile */}
            <div className="px-2 mb-6 md:hidden">
              <UserCard email={email} />
            </div>

            {/* Navigation Links */}
            <div className="flex flex-col gap-1.5 md:gap-2 flex-1 px-1">
              {links.map((link, idx) => (
                <SidebarLink key={idx} link={link} />
              ))}
            </div>
          </div>

          {/* User section et Logout - En bas pour desktop */}
          <div className="px-2 mt-auto pt-4 border-t border-slate-700/50">
            {/* User mini-card - Visible uniquement sur desktop */}
            <div className="hidden md:block mb-4">
              <UserCard email={email} />
            </div>
            <LogoutButton onLogout={handleLogout} t={t} />
          </div>
        </SidebarBody>
      </Sidebar>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen ml-0 md:ml-[90px] pt-14 md:pt-0">
        {children}
      </div>

      {/* Tutoriel global - visible sur toutes les pages */}
      <DashboardTutorial />

      {/* Extension Notification Popup */}
      <ExtensionNotificationPopup 
        isVisible={showExtensionPopup && !extensionDismissed}
        onDismiss={handleDismissExtensionPopup}
        t={t}
      />
    </div>
  );
}
